import { StandardProps } from "@/types/types";
import classNames from "classnames/bind";
import { UnderlinedButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import { CloseOutlined as Close24PxOutlinedIcon } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/CloseOutlined";
import { dispatchLegacyAnalyticsEvent } from "@/utils/analytics";
import {
  extractAppliedFilters,
  removeAllPriceQueries,
  removePriceFilter,
} from "./utils";
import styles from "./AppliedFilters.module.scss";
import { useRouteParams } from "@/hooks/useRouteParams";
import { sanitiseQueryParams } from "@/utils/queryUtils";
import { getAnalyticsPageType } from "@/utils/setJLData";
import { LinkWithoutPrefetch } from "@/components/__shared__/LinkWithoutPrefetch/LinkWithoutPrefetch";

const cx = classNames.bind(styles);

const CLEAR_FILTERS_THRESHOLD = 2;

export function AppliedFilters({
  productListingData,
  configProps,
}: Pick<StandardProps, "productListingData" | "configProps">) {
  const { baseFacetUrl, facets } = productListingData;
  const { pageType, queryWithoutPageOrChunk } = useRouteParams();

  if (facets.length === 0) return null;

  const { appliedFilters, combination, trackingIds } =
    extractAppliedFilters(facets);

  if (appliedFilters.length === 0) return null;

  // Sort labels within each facetName, but preserve facetName order
  const facetNameOrder = appliedFilters.map((filter) => filter.facetName);
  const uniqueFacetNames = [...new Set(facetNameOrder)];

  appliedFilters.sort((facetA, facetB) => {
    const facetAIndex = uniqueFacetNames.indexOf(facetA.facetName);
    const facetBIndex = uniqueFacetNames.indexOf(facetB.facetName);

    // If they're the same facetName, sort by label alphabetically
    if (facetA.facetName === facetB.facetName) {
      return facetA.label
        .toLowerCase()
        .localeCompare(facetB.label.toLowerCase());
    }

    // Otherwise, sort by facetName order
    return facetAIndex - facetBIndex;
  });

  const dispatchFacetAnalytics = (
    filterAction: string,
    facetName: string,
    label: string,
    trackingId: string,
  ) => {
    const toBeFilterValues = appliedFilters.filter(
      (f) => !(f.facetName === facetName && f.label === label),
    );

    dispatchLegacyAnalyticsEvent(filterAction, {
      pagetype: getAnalyticsPageType(pageType, productListingData.results),
      // All applied filters *after* the filter has been removed i.e. the result
      filterCombination: !facetName
        ? ""
        : toBeFilterValues
            .map((f) => `${f.facetName}:${f.label}`.toLowerCase())
            .join("|"),
      filterCombinationNvals: !facetName
        ? ""
        : toBeFilterValues.map((f) => f.trackingId).join("|"),
      // Specific filter being removed
      appliedFilter: !facetName
        ? combination
        : `${facetName}:${label}`.toLowerCase(),
      appliedFilterNval: !facetName ? trackingIds : `${trackingId}`,
    });
  };

  const rootUrl = pageType === "search" ? "/search" : baseFacetUrl;

  const clearAllFiltersQuery = sanitiseQueryParams(
    removeAllPriceQueries(queryWithoutPageOrChunk),
  );
  const clearAllFiltersHref =
    clearAllFiltersQuery.length > 0
      ? `${rootUrl}?${clearAllFiltersQuery}`
      : rootUrl;

  return (
    <div
      data-component="AppliedFilters"
      className={cx("filters", {
        "filters--desktop-only": configProps?.desktopOnly,
        "filters--mobile-only": configProps?.mobileOnly,
      })}
    >
      <h3 className={styles.header}>Applied Filters</h3>
      <ul className={styles.filterList} data-testid="applied-filters">
        {appliedFilters.map(({ facetName, label, trackingId, facetUrl }) => {
          const query = trackingId.startsWith("price=")
            ? removePriceFilter(queryWithoutPageOrChunk, trackingId)
            : queryWithoutPageOrChunk;
          return (
            <li key={label} className={styles.filter}>
              <LinkWithoutPrefetch
                href={{
                  pathname: facetUrl.split("?")[0],
                  query: sanitiseQueryParams(query),
                }}
                className={styles.filterLink}
                onClick={() =>
                  dispatchFacetAnalytics(
                    "plp.filterRemoved",
                    facetName,
                    label,
                    trackingId,
                  )
                }
              >
                <span className={styles.filterLinkText}>{label}</span>
                <div className={styles.filterLinkIcon}>
                  <Close24PxOutlinedIcon title="Remove filter" size="medium" />
                </div>
              </LinkWithoutPrefetch>
            </li>
          );
        })}
        {appliedFilters.length >= CLEAR_FILTERS_THRESHOLD && (
          <li>
            <UnderlinedButton
              onClick={() =>
                dispatchFacetAnalytics("plp.filterRemoved", "", "", "")
              }
              tag={LinkWithoutPrefetch}
              href={clearAllFiltersHref}
            >
              Clear all filters
            </UnderlinedButton>
          </li>
        )}
      </ul>
    </div>
  );
}
